import React, { useEffect, useState } from 'react';
import api, { assetUrl, formatApiError } from '../../lib/api';

const CATEGORIES = [
  { value: 'hero',           label: 'Hero (homepage top)' },
  { value: 'refrigerators',  label: 'Refrigerators' },
  { value: 'stoves',         label: 'Stoves & Ranges' },
  { value: 'washers-dryers', label: 'Washers & Dryers' },
  { value: 'microwaves',     label: 'Microwaves' },
];

const empty = { category: 'refrigerators', image: '', title_en: '', title_es: '', subtitle_en: '', subtitle_es: '', cta_url: '', active: true };

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // banner being edited, or null
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/banners').then(r => setBanners(r.data)).catch(e => setErr(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing('new'); setForm(empty); setErr(''); };
  const startEdit = (b) => { setEditing(b.id); setForm({ ...empty, ...b }); setErr(''); };
  const cancel = () => { setEditing(null); setForm(empty); setErr(''); };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image: data.url }));
    } catch (e) { setErr(formatApiError(e)); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) { setErr('Upload an image first'); return; }
    setSaving(true); setErr('');
    try {
      if (editing === 'new') {
        const { data } = await api.post('/admin/banners', form);
        setBanners(bs => [data, ...bs]);
      } else {
        const { data } = await api.patch(`/admin/banners/${editing}`, form);
        setBanners(bs => bs.map(b => b.id === editing ? data : b));
      }
      cancel();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    setBusy(id);
    try {
      await api.delete(`/admin/banners/${id}`);
      setBanners(bs => bs.filter(b => b.id !== id));
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="admin-page-head">
        <div>
          <h1>Banners</h1>
          <p className="muted">{banners.length} total — featured strips per category</p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn btn-primary" data-testid="admin-new-banner-btn">
            <i className="fa-solid fa-plus"></i><span>New banner</span>
          </button>
        )}
      </div>

      {err && <div className="form-error" style={{ marginBottom: 16 }}>{err}</div>}

      {editing && (
        <form onSubmit={save} className="admin-card" style={{ marginBottom: 20 }} data-testid="banner-form">
          <h2 className="admin-card-h">{editing === 'new' ? 'New banner' : 'Edit banner'}</h2>

          <div className="grid-2">
            <label>
              <span>Category</span>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="banner-category">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="check">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="banner-active" />
              <span>Active</span>
            </label>
          </div>

          <label>
            <span>Banner image</span>
            <div className="image-grid" style={{ gridTemplateColumns: 'minmax(220px, 360px) auto' }}>
              {form.image && (
                <div className="image-tile primary" style={{ aspectRatio: '21/9' }}>
                  <img src={assetUrl(form.image)} alt="banner" />
                </div>
              )}
              <label className="image-upload" style={{ aspectRatio: '21/9' }}>
                <input type="file" accept="image/*" onChange={e => { onUpload(e.target.files?.[0]); e.target.value = ''; }} hidden data-testid="banner-upload" />
                <i className={uploading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-cloud-arrow-up'}></i>
                <span>{uploading ? 'Uploading…' : (form.image ? 'Replace image' : 'Upload image')}</span>
              </label>
            </div>
          </label>

          <div className="grid-2">
            <label><span>Title (English)</span><input type="text" value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} data-testid="banner-title-en" /></label>
            <label><span>Título (Español)</span><input type="text" value={form.title_es} onChange={e => setForm(f => ({ ...f, title_es: e.target.value }))} data-testid="banner-title-es" /></label>
          </div>
          <div className="grid-2">
            <label><span>Subtitle (English)</span><input type="text" value={form.subtitle_en} onChange={e => setForm(f => ({ ...f, subtitle_en: e.target.value }))} /></label>
            <label><span>Subtítulo (Español)</span><input type="text" value={form.subtitle_es} onChange={e => setForm(f => ({ ...f, subtitle_es: e.target.value }))} /></label>
          </div>
          <label>
            <span>Call-to-action URL (optional)</span>
            <input type="text" value={form.cta_url} onChange={e => setForm(f => ({ ...f, cta_url: e.target.value }))} placeholder="/shop/refrigerators" />
          </label>

          <div className="admin-form-actions">
            <button type="button" onClick={cancel} className="btn btn-ghost">Cancel</button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={saving} data-testid="banner-save-btn">
              <i className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'}></i>
              <span>{saving ? 'Saving…' : (editing === 'new' ? 'Create banner' : 'Save changes')}</span>
            </button>
          </div>
        </form>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="admin-banners-table">
          <thead>
            <tr><th>Preview</th><th>Category</th><th>Title</th><th>Active</th><th></th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5}><div className="skeleton" style={{ height: 30 }} /></td></tr>}
            {!loading && banners.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No banners — click "New banner" to add one.</td></tr>
            )}
            {!loading && banners.map(b => (
              <tr key={b.id} data-testid={`banner-row-${b.id}`}>
                <td><div className="admin-thumb" style={{ width: 110, height: 50, borderRadius: 8 }}>{b.image && <img src={assetUrl(b.image)} alt={b.title_en} />}</div></td>
                <td>{b.category}</td>
                <td><strong>{b.title_en || '—'}</strong><div className="muted" style={{ fontSize: 12 }}>{b.subtitle_en || ''}</div></td>
                <td><span className={`stock-pill ${b.active ? 'in' : 'out'}`}>{b.active ? 'Active' : 'Hidden'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => startEdit(b)} className="icon-btn" title="Edit" data-testid={`banner-edit-${b.id}`}><i className="fa-solid fa-pen"></i></button>
                    <button onClick={() => remove(b.id)} className="icon-btn danger" title="Delete" disabled={busy === b.id} data-testid={`banner-delete-${b.id}`}><i className="fa-solid fa-trash"></i></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
